import { useEffect, useMemo, useState } from 'react';

import * as navigation from 'app-level/navigation';
import { filtersToQueryFilters, PillFilters } from 'features/common-elements';
import { CashFlows } from 'features/drilldown/cashflows/CashFlows';
import { getUsePortfolioCashflow } from 'features/drilldown/cashflows/gql/usePortfolioCashflows_gql';
import { PortfolioFilterConfig } from 'features/pages/portfolio/Portfolio/portfolioFilters.config';

import { GetPools_user_company_pools as Listing } from 'query/__generated__/GetPools';

import { useGetPoolsList } from './getListingsList_gql';
import { getUseSelectedPoolCashflow } from './getSelectedPoolCashflow_gql';
import { Dropdown } from '../../../../ui-kit';
import { PortfolioLayout } from '../Portfolio/PortfolioLayout';
import { usePortfolioFilters } from '../Portfolio/usePortfolioFilters';
import { transformSelectedPoolsData, usePortfolioContext } from '../PortfolioContext';

const CashFlowModeling = () => {
  const assetClass = navigation.usePortfolioAssetClass();
  const { setSelectedPoolsData } = usePortfolioContext();
  const filterProps = usePortfolioFilters();
  const [selectedPool, setSelectedPool] = useState<{
    label: string;
    value: string;
    poolData: Listing;
  } | null>(null);

  const { data: poolsList } = useGetPoolsList();

  const { poolFromUrl, setPool } = usePortfolioFilters();

  const poolOptions = useMemo(() => {
    const normalizedAssetClass = assetClass?.toLowerCase();

    return poolsList?.user?.company?.pools
      ?.filter((listing) => listing.asset_class.toLowerCase() === normalizedAssetClass)
      ?.sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      })
      ?.map((listing) => ({
        label: listing.name,
        value: listing.id,
        poolData: listing,
      }));
  }, [poolsList?.user?.company?.pools, assetClass]);

  const { setCurrentFilters, currentFilters } = filterProps;

  useEffect(() => {
    if (!poolOptions || !poolFromUrl) return;
    const selected = poolOptions.find((pool) => pool.value === poolFromUrl);
    if (selected) {
      setSelectedPool(selected);
    } else {
      setPool(null);
    }
  }, [poolOptions, poolFromUrl, setPool]);

  useEffect(() => {
    if (selectedPool) {
      setSelectedPoolsData(transformSelectedPoolsData([selectedPool.poolData]));
      setCurrentFilters([...currentFilters]);
      setPool(selectedPool.value);
    } else if (poolOptions && poolFromUrl && poolOptions.length > 0) {
      const poolFromUrlOption = poolOptions.find((pool) => pool.value === poolFromUrl);
      if (poolFromUrlOption) {
        setSelectedPool(poolFromUrlOption);
      }
    } else {
      setSelectedPoolsData([]);
    }
  }, [
    selectedPool,
    setSelectedPoolsData,
    setCurrentFilters,
    currentFilters,
    setPool,
    poolFromUrl,
    poolOptions,
  ]);

  if (!assetClass || !filterProps.filtersConfig) {
    return <PortfolioLayout />;
  }

  const queryFilters = filtersToQueryFilters<PortfolioFilterConfig>(
    filterProps.currentFilters,
    filterProps.filtersConfig,
  );
  const useCashflowSummery = getUsePortfolioCashflow(assetClass, queryFilters);
  const userSettingKey = `cashflowsportfolio${assetClass}`;
  const useSelectedPoolCashflow = getUseSelectedPoolCashflow(assetClass, queryFilters);

  const handlePoolChange = (
    option: { label: string; value: string; poolData?: Listing } | null,
  ) => {
    if (!option || !option.poolData) {
      setPool(null);
      setSelectedPool(null);
      return;
    }

    setSelectedPool(
      option as {
        label: string;
        value: string;
        poolData: Listing;
      },
    );
  };

  return (
    <PortfolioLayout
      filterRow={
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex-1">
            <PillFilters {...filterProps} filtersConfig={filterProps.filtersConfig} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Pool:</span>
            <Dropdown
              options={poolOptions || []}
              value={selectedPool?.label || ''}
              className="w-48 border-none"
              placeholder="Select Pool"
              onValueChange={handlePoolChange}
            />
          </div>
        </div>
      }
    >
      <CashFlows
        key={assetClass}
        settingKey={userSettingKey}
        useSummaryData={useCashflowSummery}
        selectedPoolId={selectedPool?.value}
        selectedPoolCashflow={useSelectedPoolCashflow}
      />
    </PortfolioLayout>
  );
};

export { CashFlowModeling };
